import { Search, Folder, Star, RefreshCw, Filter, ChevronLeft, ChevronRight, Heart, X, User, Check, Settings } from 'lucide-react';
import { formatNumber } from '@/utils/formatNumber';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { TagFilterDropdown } from '@/components/TagFilterDropdown';
import { FolderFilterDropdown } from '@/components/FolderFilterDropdown';
import { ColorFilterDropdown } from '@/components/ColorFilterDropdown';

interface ToolbarProps {
  title: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  selectedFolders?: string[];
  onFoldersChange?: (folders: string[]) => void;
  selectedColors?: string[];
  onColorsChange?: (colors: string[]) => void;
  includeSimilarColors?: boolean;
  onIncludeSimilarColorsChange?: (includeSimilar: boolean) => void;
  ratingFilter?: { operator: 'gte' | 'lte' | 'eq'; value: number } | null;
  onRatingFilterChange?: (filter: { operator: 'gte' | 'lte' | 'eq'; value: number } | null) => void;
  photoTypeFilter?: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null;
  onPhotoTypeFilterChange?: (filter: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null) => void;
  favoriteFilter?: boolean;
  onFavoriteFilterChange?: (favorite: boolean) => void;
  availableTags?: Array<{ id: string; name: string; color?: string }>;
  availableFolders?: Array<{ id: string; name: string; color?: string }>;
  thumbnailSize?: 'small' | 'medium' | 'large';
  onThumbnailSizeChange?: (size: 'small' | 'medium' | 'large') => void;
  onReprocessFailed?: () => void;
  onReprocessAll?: () => void;
  onReprocessNoTags?: () => void;
  organizationId?: string;
  onTagCreated?: () => void;
  sortField?: 'created_at' | 'updated_at' | 'filename' | 'rating' | 'date_taken';
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (field: 'created_at' | 'updated_at' | 'filename' | 'rating' | 'date_taken', direction: 'asc' | 'desc') => void;
  userEmail?: string;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onSignOut?: () => void;
  onClearAllFilters?: () => void;
}

export function Toolbar({
  title,
  searchQuery,
  onSearchChange,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  selectedTags = [],
  onTagsChange,
  selectedFolders = [],
  onFoldersChange,
  selectedColors = [],
  onColorsChange,
  includeSimilarColors = false,
  onIncludeSimilarColorsChange,
  ratingFilter,
  onRatingFilterChange,
  photoTypeFilter = 'all',
  onPhotoTypeFilterChange,
  favoriteFilter = false,
  onFavoriteFilterChange,
  availableTags = [],
  availableFolders = [],
  thumbnailSize = 'medium',
  onThumbnailSizeChange,
  onReprocessFailed,
  onReprocessAll,
  onReprocessNoTags,
  organizationId,
  onTagCreated,
  sortField = 'created_at',
  sortDirection = 'desc',
  onSortChange,
  userEmail,
  onProfileClick,
  onSettingsClick,
  onSignOut,
  onClearAllFilters,
}: ToolbarProps) {
  const toggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange?.(selectedTags.filter(id => id !== tagId));
    } else {
      onTagsChange?.([...selectedTags, tagId]);
    }
  };

  const toggleFolder = (folderId: string) => {
    if (selectedFolders.includes(folderId)) {
      onFoldersChange?.(selectedFolders.filter(id => id !== folderId));
    } else {
      onFoldersChange?.([...selectedFolders, folderId]);
    }
  };

  const hasActiveFilters = selectedTags.length > 0 || 
    selectedFolders.length > 0 || 
    selectedColors.length > 0 ||
    ratingFilter !== null || 
    favoriteFilter ||
    searchQuery.trim() !== '' ||
    (photoTypeFilter && photoTypeFilter !== 'all');

  const clearAllFilters = () => {
    if (onClearAllFilters) {
      onClearAllFilters();
    } else {
      // Fallback if onClearAllFilters not provided
      onSearchChange('');
      onTagsChange?.([]);
      onFoldersChange?.([]);
      onColorsChange?.([]);
      onIncludeSimilarColorsChange?.(false);
      onRatingFilterChange?.(null);
      onPhotoTypeFilterChange?.('all');
      onFavoriteFilterChange?.(false);
    }
  };

  return (
    <div className="h-14 border-b bg-card flex items-center justify-between px-[7px] gap-4 flex-shrink-0">
      {/* Left section - Navigation and Search */}
      <div className="flex items-center gap-2 flex-1">
        {/* Previous/Next Buttons */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="h-9 w-9 flex-shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="h-9 w-9 flex-shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Search - 2x size */}
        <div className="relative flex-shrink-0" style={{ width: '400px' }}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-9 h-9 w-full"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onSearchChange('')}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Filter Type */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 relative">
              <Filter className="h-4 w-4" />
              {photoTypeFilter && photoTypeFilter !== 'all' && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                  1
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40 bg-background z-50">
            <DropdownMenuLabel>Filter Type</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('all')} className="relative">
              {photoTypeFilter === 'all' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'all' ? 'ml-6' : ''}>All</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_pii')} className="relative">
              {photoTypeFilter === 'has_pii' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'has_pii' ? 'ml-6' : ''}>Has PII</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('no_tags')} className="relative">
              {photoTypeFilter === 'no_tags' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'no_tags' ? 'ml-6' : ''}>No Tags</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('no_folders')} className="relative">
              {photoTypeFilter === 'no_folders' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'no_folders' ? 'ml-6' : ''}>No Folders</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_tag')} className="relative">
              {photoTypeFilter === 'has_tag' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'has_tag' ? 'ml-6' : ''}>Has Tag</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_code')} className="relative">
              {photoTypeFilter === 'has_code' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'has_code' ? 'ml-6' : ''}>Code</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_person')} className="relative">
              {photoTypeFilter === 'has_person' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'has_person' ? 'ml-6' : ''}>Person</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('is_illustrated')} className="relative">
              {photoTypeFilter === 'is_illustrated' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'is_illustrated' ? 'ml-6' : ''}>Illustrated</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPhotoTypeFilterChange?.('has_error')} className="relative">
              {photoTypeFilter === 'has_error' && <Check className="absolute left-2 h-4 w-4" />}
              <span className={photoTypeFilter === 'has_error' ? 'ml-6' : ''}>Error</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Color Filter */}
        <ColorFilterDropdown
          selectedColors={selectedColors}
          onColorsChange={onColorsChange || (() => {})}
          includeSimilar={includeSimilarColors}
          onIncludeSimilarChange={onIncludeSimilarColorsChange || (() => {})}
          organizationId={organizationId || ''}
        />

        {/* Tag Filter */}
        <TagFilterDropdown
          selectedTags={selectedTags}
          onTagsChange={onTagsChange || (() => {})}
          availableTags={availableTags}
          organizationId={organizationId || ''}
          onTagCreated={onTagCreated}
        />

        {/* Folder Filter */}
        <FolderFilterDropdown
          selectedFolders={selectedFolders}
          onFoldersChange={onFoldersChange || (() => {})}
          organizationId={organizationId}
        />

        {/* Star Rating Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 relative">
              <Star className="h-4 w-4" />
              {ratingFilter && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
                  1
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-background z-50">
            <DropdownMenuLabel>Filter by Rating</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 5 })}>
              ★★★★★ (5 stars)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 4 })}>
              ★★★★ (4+ stars)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 3 })}>
              ★★★ (3+ stars)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 2 })}>
              ★★ (2+ stars)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'gte', value: 1 })}>
              ★ (1+ stars)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 1 })}>
              ≤ 1 star
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 2 })}>
              ≤ 2 stars
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRatingFilterChange?.({ operator: 'lte', value: 3 })}>
              ≤ 3 stars
            </DropdownMenuItem>
            {ratingFilter && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onRatingFilterChange?.(null)}>
                  Clear filter
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Heart/Favorite Filter */}
        <Button 
          variant="ghost" 
          size="icon" 
          className={`h-8 w-8 relative ${favoriteFilter ? 'text-red-500' : ''}`}
          onClick={() => onFavoriteFilterChange?.(!favoriteFilter)}
        >
          <Heart className={`h-4 w-4 ${favoriteFilter ? 'fill-current' : ''}`} />
          {favoriteFilter && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
              1
            </Badge>
          )}
        </Button>

        {/* Reprocess/Sync Menu */}
        {(onReprocessFailed || onReprocessAll || onReprocessNoTags) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Reprocess photos"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background z-50">
              <DropdownMenuLabel>Reprocess Photos</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {onReprocessFailed && (
                <DropdownMenuItem onClick={onReprocessFailed}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess Failed
                </DropdownMenuItem>
              )}
              {onReprocessAll && (
                <DropdownMenuItem onClick={onReprocessAll}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess All (Add Embeddings)
                </DropdownMenuItem>
              )}
              {onReprocessNoTags && (
                <DropdownMenuItem onClick={onReprocessNoTags}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess No Tags
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

         {/* Clear All Filters - Purple Button */}
         {hasActiveFilters && (
           <Button
             variant="default"
             size="sm"
             onClick={clearAllFilters}
             className="h-8 text-xs gap-1 bg-purple-600 hover:bg-purple-700 text-white"
           >
             <X className="h-3 w-3" />
             Clear all
           </Button>
        )}
      </div>

      {/* Right section - User Menu */}
      {userEmail && (
        <div className="flex items-center gap-2 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <User className="h-6 w-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background z-50">
              <DropdownMenuLabel>{userEmail}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {onProfileClick && (
                <DropdownMenuItem onClick={onProfileClick}>
                  Profile Settings
                </DropdownMenuItem>
              )}
              {onSettingsClick && (
                <DropdownMenuItem onClick={onSettingsClick}>
                  <Settings className="h-4 w-4 mr-2" />
                  Configuration
                </DropdownMenuItem>
              )}
              {onSignOut && (
                <>
                  {(onProfileClick || onSettingsClick) && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={onSignOut}>
                    Sign out
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
